import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { FlightProfile } from "../../core/decorFlights";

const STEP_RISE = 0.17;
const STEP_TREAD = 0.3;
const TOP_LANDING = 0.9;
const MIDDLE_LANDING = 1.8;
const NOSING = 0.03;

export type SolidStairsFinish = "wood" | "concrete";

const FINISHES: Record<SolidStairsFinish, { body: string; nosing: string }> = {
  wood: { body: "#f3cca8", nosing: "#d7b495" },
  concrete: { body: "#cfccc5", nosing: "#a9a69f" },
};

const materials = new Map<SolidStairsFinish, { body: THREE.Material; nosing: THREE.Material }>();

function finishMaterials(finish: SolidStairsFinish) {
  let found = materials.get(finish);
  if (!found) {
    const colors = FINISHES[finish];
    found = {
      body: new THREE.MeshStandardMaterial({ name: "steps", color: colors.body, roughness: 0.9, metalness: 0 }),
      nosing: new THREE.MeshStandardMaterial({ name: "nosing", color: colors.nosing, roughness: 0.9, metalness: 0 }),
    };
    materials.set(finish, found);
  }
  return found;
}

function box(width: number, height: number, depth: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const geometry = new THREE.BoxGeometry(width, height, depth);
  geometry.translate(x, y, z);
  return geometry;
}

function merged(geometries: THREE.BufferGeometry[], material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(mergeGeometries(geometries.map((geometry) => geometry.toNonIndexed())), material);
  mesh.name = name;
  return mesh;
}

function stepCount(rise: number): number {
  return Math.max(2, Math.round(rise / STEP_RISE));
}

/** Marches de la volée du haut quand l'escalier a un palier intermédiaire. */
function upperSteps(steps: number): number {
  return Math.ceil(steps / 2);
}

/** Longueur au sol d'un escalier plein de ce dénivelé, paliers compris. */
export function solidStairsLandings(landing: boolean): number {
  return TOP_LANDING + (landing ? MIDDLE_LANDING : 0);
}

export function solidStairsLength(rise: number, landing: boolean, tread = STEP_TREAD): number {
  return solidStairsLandings(landing) + stepCount(rise) * tread;
}

/** Hauteur de l'escalier le long de sa descente, paliers compris : voir `FlightProfile`. */
export function solidStairsProfile(rise: number, landing: boolean, tread = STEP_TREAD): FlightProfile {
  const length = solidStairsLength(rise, landing, tread);
  if (!landing) return [[0, rise], [TOP_LANDING, rise], [length, 0]];

  const steps = stepCount(rise);
  const upper = upperSteps(steps);
  const middleY = rise - upper * (rise / steps);
  const middle = TOP_LANDING + upper * tread;
  return [[0, rise], [TOP_LANDING, rise], [middle, middleY], [middle + MIDDLE_LANDING, middleY], [length, 0]];
}

/**
 * Escalier plein, maçonné jusqu'au sol, de `rise` mètres de haut : on y entre côté −z par le palier
 * du haut, il descend vers +z, d'une seule volée ou de deux séparées par un palier. Mesures en mètres.
 */
export function buildSolidStairs(width: number, rise: number, options: { landing: boolean; finish: SolidStairsFinish; tread?: number }): THREE.Group {
  const tread = options.tread ?? STEP_TREAD;
  const steps = stepCount(rise);
  const stepRise = rise / steps;
  const upper = options.landing ? upperSteps(steps) : steps;
  const middleY = rise - upper * stepRise;
  const z0 = -solidStairsLength(rise, options.landing, tread) / 2;
  const middleZ = z0 + TOP_LANDING + upper * tread;

  const blocks: THREE.BufferGeometry[] = [box(width, rise, TOP_LANDING, 0, rise / 2, z0 + TOP_LANDING / 2)];
  if (options.landing) blocks.push(box(width, middleY, MIDDLE_LANDING, 0, middleY / 2, middleZ + MIDDLE_LANDING / 2));

  const noses: THREE.BufferGeometry[] = [];
  for (let i = 0; i < steps; i++) {
    const top = rise - (i + 1) * stepRise;
    const z = i < upper
      ? z0 + TOP_LANDING + i * tread
      : middleZ + MIDDLE_LANDING + (i - upper) * tread;
    if (top <= 0) continue;
    blocks.push(box(width, top, tread, 0, top / 2, z + tread / 2));
    noses.push(box(width, NOSING, NOSING, 0, top - NOSING / 2, z + NOSING / 2));
  }

  const { body, nosing } = finishMaterials(options.finish);
  const root = new THREE.Group();
  root.add(merged(blocks, body, "Steps"));
  if (noses.length) root.add(merged(noses, nosing, "Nosing"));
  return root;
}
